/**
 * Site: activities and progress, the site diary, and handover.
 */

import { request } from './client.js';
import { pageCall, type RegisterPage } from './paging.js';

// ------------------------------------------------------------------ activities

export type ActivityStatus = 'not_started' | 'in_progress' | 'complete' | 'waived';

export interface Activity {
  readonly id: string;
  readonly name: string;
  readonly location_id: string | null;
  readonly location_path: string | null;
  readonly trade_id: string | null;
  readonly trade_name: string | null;
  readonly planned_start: string | null;
  readonly planned_finish: string | null;
  readonly forecast_finish: string | null;
  readonly actual_start: string | null;
  readonly actual_finish: string | null;
  /** A decimal STRING, like every other figure here. Never a JSON number. */
  readonly percent_complete: string;
  readonly budget_weight: string;
  readonly waived_at: string | null;
  readonly waiver_reason: string | null;
  readonly waived_by_name: string | null;
  /** Derived by the server, never stored — see `site/activities.ts`. */
  readonly status: ActivityStatus;
  readonly days_late: number;
  readonly progress_count: number;
}

export interface ProgressRecord {
  readonly id: string;
  readonly entry_date: string;
  readonly percent_complete: string;
  readonly delta: string;
  readonly note: string | null;
  readonly recorded_by_name: string | null;
  readonly recorded_at: string;
}

/**
 * The server returns the activity list BARE, like every other register route.
 * This wrapper used to unwrap a `{ data }` envelope that never existed on the
 * wire — `items` became `undefined`, skipped the null loading guard, and
 * `items.length` blanked the whole project page. Found by the E2E suite on its
 * first run against the real server; the unit tests mock this module and the
 * integration tests stop at the service, so neither could see it.
 */
function activityCall(path: string, init?: RequestInit): Promise<readonly Activity[]> {
  return request<readonly Activity[]>(path, init ?? {});
}

const actPath = (projectId: string): string =>
  `/v1/projects/${encodeURIComponent(projectId)}/activities`;

/**
 * The programme, which the server bounds.
 *
 * The total is what SURVIVES the lookahead window and the work-package scope,
 * not the table's row count — both filters are applied server-side in
 * TypeScript. So "the first 500 of 620" means 620 activities this reader can
 * see in this window, which is the only number worth printing. See
 * `api/paging.ts`.
 */
export function listActivities(
  projectId: string,
  options: { lookahead?: boolean } = {},
  limit?: number,
): Promise<RegisterPage<Activity>> {
  return pageCall<Activity>(
    `${actPath(projectId)}${options.lookahead ? '?lookahead=true' : ''}`,
    limit,
  );
}

export function createActivity(
  projectId: string,
  input: {
    name: string;
    location_id?: string | null;
    trade_id?: string | null;
    planned_start?: string | null;
    planned_finish?: string | null;
    budget_weight?: string;
  },
): Promise<readonly Activity[]> {
  return activityCall(actPath(projectId), { method: 'POST', body: JSON.stringify(input) });
}

/** NO `percent_complete`. Progress is recorded, not edited — see `recordProgress`. */
export function updateActivity(
  projectId: string,
  activityId: string,
  input: {
    name?: string;
    location_id?: string | null;
    trade_id?: string | null;
    planned_start?: string | null;
    planned_finish?: string | null;
    forecast_finish?: string | null;
    budget_weight?: string;
  },
): Promise<readonly Activity[]> {
  return activityCall(`${actPath(projectId)}/${encodeURIComponent(activityId)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** The only way the figure moves. Writes a permanent entry with who and when. */
export function recordProgress(
  projectId: string,
  activityId: string,
  input: { percent_complete: string; entry_date?: string; note?: string | null },
): Promise<readonly Activity[]> {
  return activityCall(`${actPath(projectId)}/${encodeURIComponent(activityId)}/progress`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function listProgress(
  projectId: string,
  activityId: string,
): Promise<readonly ProgressRecord[]> {
  return request<readonly ProgressRecord[]>(
    `${actPath(projectId)}/${encodeURIComponent(activityId)}/progress`,
  );
}

/** Its own call, because it is its own act — the thing that opens the Site gate. */
export function waiveActivity(
  projectId: string,
  activityId: string,
  reason: string,
): Promise<readonly Activity[]> {
  return activityCall(`${actPath(projectId)}/${encodeURIComponent(activityId)}/waive`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
}

export function unwaiveActivity(
  projectId: string,
  activityId: string,
): Promise<readonly Activity[]> {
  return activityCall(`${actPath(projectId)}/${encodeURIComponent(activityId)}/waive`, {
    method: 'DELETE',
  });
}

export function deleteActivity(
  projectId: string,
  activityId: string,
): Promise<readonly Activity[]> {
  return activityCall(`${actPath(projectId)}/${encodeURIComponent(activityId)}`, {
    method: 'DELETE',
  });
}

// ------------------------------------------------------------------ site diary

export type CoverageStatus = 'not_started' | 'green' | 'amber' | 'red';

/**
 * The Site signal's diary input (`06` §Signal 4).
 *
 * `not_started` is deliberately NOT green: a project with no site data is
 * unknown, and unknown is a risk.
 */
export interface DiaryCoverage {
  readonly status: CoverageStatus;
  /** Consecutive working days ending yesterday with no submitted diary. */
  readonly gap_working_days: number;
  readonly last_entry_date: string | null;
  readonly missing_dates: readonly string[];
  readonly window_start: string | null;
  readonly window_end: string;
  readonly expected_working_days: number;
  readonly present_working_days: number;
  readonly started_on: string | null;
  readonly started_by: 'first_diary' | 'site_stage' | null;
  /** 0 = Sunday … 6 = Saturday. Days nobody is expected to write up. */
  readonly rest_days: readonly number[];
  readonly thresholds: { readonly amber: number; readonly red: number };
}

export interface ManpowerLine {
  readonly trade_id: string;
  readonly trade_name: string;
  readonly headcount: number;
  readonly note: string | null;
}

export interface DiaryEntry {
  readonly id: string;
  readonly date: string;
  readonly work_summary: string | null;
  readonly delays_note: string | null;
  readonly visitors_note: string | null;
  readonly submitted_at: string | null;
  readonly submitted_by_name: string | null;
  readonly author_name: string | null;
  readonly edited_after_submission: boolean;
  readonly edited_at: string | null;
  /** Days between the day it covers and the day it was written. Zero is normal. */
  readonly recorded_days_late: number;
  readonly manpower: readonly ManpowerLine[];
  readonly total_manpower: number;
  /** What was worked that day. Says work happened, NOT how far along it is. */
  readonly activities: readonly WorkedActivity[];
}

export interface WorkedActivity {
  readonly activity_id: string;
  readonly name: string;
}

export type DayState = 'submitted' | 'draft' | 'missing' | 'awaited' | 'rest_day';

export interface DiaryDay {
  readonly date: string;
  /** 0 = Sunday … 6 = Saturday, from the server, so the screen never re-parses a date. */
  readonly weekday: number;
  readonly is_working_day: boolean;
  readonly state: DayState;
  readonly entry: DiaryEntry | null;
}

export interface DiaryView {
  readonly days: readonly DiaryDay[];
  readonly coverage: DiaryCoverage;
  readonly trades: readonly { readonly id: string; readonly name: string }[];
  /** What the WORK DONE picker offers: everything not finished and not waived. */
  readonly open_activities: readonly WorkedActivity[];
  readonly today: string;
  readonly timezone: string;
}

export interface DiaryInput {
  readonly date?: string;
  readonly work_summary?: string | null;
  readonly delays_note?: string | null;
  readonly visitors_note?: string | null;
  readonly manpower?: readonly { trade: string; headcount: number; note?: string | null }[];
  /** The whole WORK DONE list. Sending it replaces what was there. */
  readonly activity_ids?: readonly string[];
}

/**
 * Every diary call answers with the whole view.
 *
 * Submitting one day changes the project's site health, so a screen that
 * patched a single row would leave a stale verdict at the top of it.
 */
const diaryPath = (projectId: string): string =>
  `/v1/projects/${encodeURIComponent(projectId)}/diary`;

export function listDiary(projectId: string): Promise<DiaryView> {
  return request<DiaryView>(diaryPath(projectId));
}

export function createDiaryEntry(projectId: string, input: DiaryInput): Promise<DiaryView> {
  return request<DiaryView>(diaryPath(projectId), {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function updateDiaryEntry(
  projectId: string,
  date: string,
  input: DiaryInput,
): Promise<DiaryView> {
  return request<DiaryView>(`${diaryPath(projectId)}/${encodeURIComponent(date)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

/** Notes become the record. There is no way back — see `site/diary.ts`. */
export function submitDiaryEntry(projectId: string, date: string): Promise<DiaryView> {
  return request<DiaryView>(`${diaryPath(projectId)}/${encodeURIComponent(date)}/submit`, {
    method: 'POST',
  });
}

export function deleteDiaryEntry(projectId: string, date: string): Promise<DiaryView> {
  return request<DiaryView>(`${diaryPath(projectId)}/${encodeURIComponent(date)}`, {
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// Handover (HO-01/02/03)
// ---------------------------------------------------------------------------

export const HANDOVER_CATEGORIES = [
  'as_builts',
  'om_manuals',
  'warranties',
  'training',
  'keys_assets',
  'other',
] as const;
export type HandoverCategory = (typeof HANDOVER_CATEGORIES)[number];

export const SIGNOFF_PARTIES = ['client', 'consultant', 'main_contractor', 'other'] as const;
export type SignoffParty = (typeof SIGNOFF_PARTIES)[number];

export interface HandoverItem {
  readonly id: string;
  readonly category: HandoverCategory;
  readonly title: string;
  readonly is_mandatory: boolean;
  readonly sequence: number;
  readonly note: string | null;
  readonly completed_at: string | null;
  readonly completed_by_name: string | null;
}

export interface HandoverSignoff {
  readonly id: string;
  readonly party: SignoffParty;
  readonly party_name: string;
  readonly signed_by_name: string | null;
  readonly signed_on: string | null;
  readonly captured_at: string | null;
  readonly document_id: string | null;
  readonly note: string | null;
}

export interface HandoverReadiness {
  /** 0–100, or null while the checklist has not been set up. */
  readonly percent: number | null;
  readonly not_set_up: boolean;
  readonly checklist_done: number;
  readonly checklist_total: number;
  readonly snags_open: number;
  readonly ncrs_open: number;
}

export interface HandoverView {
  readonly items: readonly HandoverItem[];
  readonly signoffs: readonly HandoverSignoff[];
  readonly readiness: HandoverReadiness;
  readonly dlp_start_date: string | null;
  readonly dlp_months: number;
}

const handoverPath = (projectId: string): string =>
  `/v1/projects/${encodeURIComponent(projectId)}/handover`;

export function getHandover(projectId: string): Promise<HandoverView> {
  return request<HandoverView>(handoverPath(projectId));
}

export function seedHandoverChecklist(projectId: string): Promise<HandoverView> {
  return request<HandoverView>(`${handoverPath(projectId)}/checklist`, { method: 'POST' });
}

export function addHandoverItem(
  projectId: string,
  input: { title: string; category?: string; is_mandatory?: boolean; note?: string | null },
): Promise<HandoverView> {
  return request<HandoverView>(`${handoverPath(projectId)}/items`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export function setHandoverItemComplete(
  projectId: string,
  itemId: string,
  complete: boolean,
): Promise<HandoverView> {
  return request<HandoverView>(`${handoverPath(projectId)}/items/${encodeURIComponent(itemId)}`, {
    method: 'PATCH',
    body: JSON.stringify({ complete }),
  });
}

export function deleteHandoverItem(projectId: string, itemId: string): Promise<HandoverView> {
  return request<HandoverView>(`${handoverPath(projectId)}/items/${encodeURIComponent(itemId)}`, {
    method: 'DELETE',
  });
}

export function addHandoverSignoff(
  projectId: string,
  input: { party?: string; party_name: string; note?: string | null },
): Promise<HandoverView> {
  return request<HandoverView>(`${handoverPath(projectId)}/signoffs`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** The `approve` action — held by the client, whose acceptance this records. */
export function captureHandoverSignoff(
  projectId: string,
  signoffId: string,
  input: { signed_by_name: string; signed_on: string; document_id?: string | null },
): Promise<HandoverView> {
  return request<HandoverView>(
    `${handoverPath(projectId)}/signoffs/${encodeURIComponent(signoffId)}/capture`,
    { method: 'POST', body: JSON.stringify(input) },
  );
}

export function deleteHandoverSignoff(
  projectId: string,
  signoffId: string,
): Promise<HandoverView> {
  return request<HandoverView>(
    `${handoverPath(projectId)}/signoffs/${encodeURIComponent(signoffId)}`,
    { method: 'DELETE' },
  );
}

export function setHandoverDlp(
  projectId: string,
  input: { start_date: string; months?: number },
): Promise<HandoverView> {
  return request<HandoverView>(`${handoverPath(projectId)}/dlp`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}
